import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Menu,
  NumberInput,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@apollo/client/react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCopy,
  IconDots,
  IconPlus,
  IconTemplate,
  IconUser,
} from "@tabler/icons-react";
import { useConfirmModal } from "../../../../../components/ConfirmModal";
import { PresetParameterType } from "../../../preset-scripts/PresetParametersEditor";
import {
  APPLY_BUILTIN_PRESET_MUTATION,
  APPLY_PRESET_SCRIPT_MUTATION,
  DUPLICATE_BUILTIN_PRESET_MUTATION,
} from "../graphql/mutations";
import {
  BUILTIN_PRESETS_QUERY,
  PRESET_SCRIPTS_QUERY,
  STEP_RULE_SET_QUERY,
} from "../graphql/queries";
import {
  BuiltinPresetCategory,
  BUILTIN_PRESET_CATEGORY_LABELS,
  BUILTIN_PRESET_CATEGORY_ORDER,
} from "../builtin-preset.constants";

type PresetParameterValue = string | number | boolean;

interface PresetParameter {
  name: string;
  type: PresetParameterType;
  required: boolean;
  defaultValue: string | null;
  description: string | null;
}

interface PresetScript {
  id: string;
  name: string;
  description: string | null;
  script: string;
  parameters: PresetParameter[] | null;
  createdAt: string;
  updatedAt: string;
}

interface BuiltinPreset {
  id: string;
  displayName: string;
  description: string;
  category: BuiltinPresetCategory;
  supportsPinning: boolean;
  supportsGroupPinning: boolean | null;
  supportsSlotPinning: boolean | null;
  parameters: PresetParameter[];
}

interface PresetScriptsQueryResult {
  presetScripts: PresetScript[];
}

interface BuiltinPresetsQueryResult {
  builtinPresets: BuiltinPreset[];
}

interface StepRuleSetQueryResult {
  stepRuleSet: { id: string } | null;
}

interface DuplicateBuiltinPresetMutationResult {
  duplicateBuiltinPreset: { id: string; name: string };
}

type PresetSelectorTab = "templates" | "user";

type PresetSource =
  | { kind: "builtin"; builtin: BuiltinPreset }
  | { kind: "user"; user: PresetScript };

interface PresetSourceView {
  label: string;
  description: string | null;
  parameters: PresetParameter[];
  appliedNotificationTitle: string;
}

interface PresetSelectorProps {
  stepId: string;
  onApplied?: () => void;
}

const isPresetSelectorTab = (value: string | null): value is PresetSelectorTab =>
  value === "templates" || value === "user";

const viewPresetSource = (source: PresetSource): PresetSourceView =>
  source.kind === "builtin"
    ? {
        label: source.builtin.displayName,
        description: source.builtin.description,
        parameters: source.builtin.parameters,
        appliedNotificationTitle: "Template applied",
      }
    : {
        label: source.user.name,
        description: source.user.description ?? null,
        parameters: source.user.parameters ?? [],
        appliedNotificationTitle: "Preset applied",
      };

const parseDefaultValue = (param: PresetParameter): PresetParameterValue => {
  const raw = param.defaultValue;
  switch (param.type) {
    case PresetParameterType.INT: {
      if (raw === null || raw === "") return "";
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : "";
    }
    case PresetParameterType.FLOAT: {
      if (raw === null || raw === "") return "";
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : "";
    }
    case PresetParameterType.BOOLEAN:
      return raw === "true" || raw === "1";
    case PresetParameterType.STRING:
    default:
      return raw ?? "";
  }
};

const buildInitialValues = (
  parameters: PresetParameter[],
): Record<string, PresetParameterValue> => {
  const initial: Record<string, PresetParameterValue> = {};
  for (const param of parameters) {
    initial[param.name] = parseDefaultValue(param);
  }
  return initial;
};

const validateParameters =
  (parameters: PresetParameter[]) =>
  (
    values: Record<string, PresetParameterValue>,
  ): Record<string, string | null> => {
    const errors: Record<string, string | null> = {};
    for (const param of parameters) {
      if (!param.required) continue;
      const value = values[param.name];
      if (param.type === PresetParameterType.STRING) {
        if (typeof value !== "string" || value.trim().length === 0) {
          errors[param.name] = "This field is required";
        }
      } else if (
        param.type === PresetParameterType.INT ||
        param.type === PresetParameterType.FLOAT
      ) {
        if (typeof value !== "number" || !Number.isFinite(value)) {
          errors[param.name] = "A numeric value is required";
        }
      }
    }
    return errors;
  };

const serializeParameterValues = (
  parameters: PresetParameter[],
  values: Record<string, PresetParameterValue>,
): Record<string, PresetParameterValue> => {
  const payload: Record<string, PresetParameterValue> = {};
  for (const param of parameters) {
    const value = values[param.name];
    if (
      (param.type === PresetParameterType.INT ||
        param.type === PresetParameterType.FLOAT) &&
      typeof value !== "number"
    ) {
      // Omit empty numeric fields so the backend falls back to defaults.
      continue;
    }
    if (
      param.type === PresetParameterType.STRING &&
      typeof value === "string" &&
      value.trim().length === 0
    ) {
      continue;
    }
    payload[param.name] = value;
  }
  return payload;
};

interface PresetParameterFormProps {
  source: PresetSource;
  onSubmit: (values: Record<string, PresetParameterValue>) => Promise<void>;
  onCancel: () => void;
}

const PresetParameterForm = ({
  source,
  onSubmit,
  onCancel,
}: PresetParameterFormProps) => {
  const { description, parameters } = useMemo(
    () => viewPresetSource(source),
    [source],
  );
  const [applying, setApplying] = useState<boolean>(false);

  const form = useForm<Record<string, PresetParameterValue>>({
    initialValues: buildInitialValues(parameters),
    validate: validateParameters(parameters),
  });

  const handleSubmit = async (values: Record<string, PresetParameterValue>) => {
    setApplying(true);
    try {
      await onSubmit(values);
    } catch {
      setApplying(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
        {parameters.length === 0 ? (
          <Text size="sm" c="dimmed">
            This preset has no configurable parameters.
          </Text>
        ) : (
          parameters.map((param) => {
            const label = param.required ? `${param.name} *` : param.name;
            const fieldDescription = param.description ?? undefined;
            const value = form.values[param.name];

            if (param.type === PresetParameterType.BOOLEAN) {
              return (
                <Switch
                  key={param.name}
                  label={label}
                  description={fieldDescription}
                  checked={Boolean(value)}
                  onChange={(event) =>
                    form.setFieldValue(param.name, event.currentTarget.checked)
                  }
                />
              );
            }

            if (
              param.type === PresetParameterType.INT ||
              param.type === PresetParameterType.FLOAT
            ) {
              const numericValue: number | "" =
                typeof value === "number" ? value : "";
              return (
                <NumberInput
                  key={param.name}
                  label={label}
                  description={fieldDescription}
                  value={numericValue}
                  allowDecimal={param.type === PresetParameterType.FLOAT}
                  onChange={(next) => {
                    // Mantine NumberInput can emit strings during intermediate
                    // typing (e.g. "-" or "1."). Normalize to number or "" so
                    // our PresetParameterValue contract holds and numeric
                    // validation behaves predictably.
                    if (typeof next === "number") {
                      form.setFieldValue(param.name, next);
                    } else if (next === "" || next === "-") {
                      form.setFieldValue(param.name, "");
                    } else {
                      const parsed =
                        param.type === PresetParameterType.INT
                          ? parseInt(next, 10)
                          : parseFloat(next);
                      form.setFieldValue(
                        param.name,
                        Number.isFinite(parsed) ? parsed : "",
                      );
                    }
                  }}
                  error={form.errors[param.name]}
                  withAsterisk={param.required}
                />
              );
            }

            return (
              <TextInput
                key={param.name}
                label={label}
                description={fieldDescription}
                value={typeof value === "string" ? value : ""}
                onChange={(event) =>
                  form.setFieldValue(param.name, event.currentTarget.value)
                }
                error={form.errors[param.name]}
                withAsterisk={param.required}
              />
            );
          })
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onCancel} disabled={applying}>
            Cancel
          </Button>
          <Button type="submit" loading={applying}>
            Apply
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export const PresetSelector = ({ stepId, onApplied }: PresetSelectorProps) => {
  const navigate = useNavigate();
  const { confirm } = useConfirmModal();

  const [activeTab, setActiveTab] = useState<PresetSelectorTab>("templates");

  const {
    data: builtinsData,
    loading: builtinsLoading,
    error: builtinsError,
  } = useQuery<BuiltinPresetsQueryResult>(BUILTIN_PRESETS_QUERY);

  const {
    data: presetsData,
    loading: presetsLoading,
    error: presetsError,
  } = useQuery<PresetScriptsQueryResult>(PRESET_SCRIPTS_QUERY);

  const { data: ruleSetData } = useQuery<StepRuleSetQueryResult>(
    STEP_RULE_SET_QUERY,
    {
      variables: { tournamentStepId: stepId },
      skip: !stepId,
    },
  );

  const [applyBuiltin] = useMutation(APPLY_BUILTIN_PRESET_MUTATION, {
    refetchQueries: [
      { query: STEP_RULE_SET_QUERY, variables: { tournamentStepId: stepId } },
    ],
    awaitRefetchQueries: true,
  });

  const [applyPresetScript] = useMutation(APPLY_PRESET_SCRIPT_MUTATION, {
    refetchQueries: [
      { query: STEP_RULE_SET_QUERY, variables: { tournamentStepId: stepId } },
    ],
    awaitRefetchQueries: true,
  });

  const [duplicateBuiltin] = useMutation<DuplicateBuiltinPresetMutationResult>(
    DUPLICATE_BUILTIN_PRESET_MUTATION,
    {
      refetchQueries: [{ query: PRESET_SCRIPTS_QUERY }],
      awaitRefetchQueries: true,
    },
  );

  const userPresets: PresetScript[] = presetsData?.presetScripts ?? [];
  const builtinPresets: BuiltinPreset[] = builtinsData?.builtinPresets ?? [];
  const hasExistingRuleSet: boolean = Boolean(ruleSetData?.stepRuleSet);

  // Exhaustive Record keyed by every BuiltinPresetCategory — adding a new
  // enum value is a compile error here AND in BUILTIN_PRESET_CATEGORY_ORDER
  // (length-bounded tuple in builtin-preset.constants), so both lists stay
  // in sync without runtime assertions.
  const grouped: Record<BuiltinPresetCategory, BuiltinPreset[]> = useMemo(() => {
    const out: Record<BuiltinPresetCategory, BuiltinPreset[]> = {
      [BuiltinPresetCategory.SINGLE_ELIMINATION]: [],
      [BuiltinPresetCategory.DOUBLE_ELIMINATION]: [],
      [BuiltinPresetCategory.SWISS]: [],
      [BuiltinPresetCategory.ROUND_ROBIN]: [],
      [BuiltinPresetCategory.GROUP_STAGE]: [],
      [BuiltinPresetCategory.OTHER]: [],
    };
    for (const preset of builtinPresets) {
      out[preset.category].push(preset);
    }
    return out;
  }, [builtinPresets]);

  const confirmReplaceIfNeeded = async (label: string): Promise<boolean> => {
    if (!hasExistingRuleSet) return true;
    return confirm({
      title: "Replace existing rules?",
      message: `Applying "${label}" will replace the current rule set for this step. This action cannot be undone.`,
      confirmLabel: "Replace",
      cancelLabel: "Cancel",
      type: "warning",
    });
  };

  const openPresetModal = (source: PresetSource) => {
    const view = viewPresetSource(source);
    const modalId = `apply-preset-${
      source.kind === "builtin" ? source.builtin.id : source.user.id
    }`;

    const handleApply = async (
      values: Record<string, PresetParameterValue>,
    ): Promise<void> => {
      const okToReplace = await confirmReplaceIfNeeded(view.label);
      if (!okToReplace) return;

      try {
        const serializedParams = JSON.stringify(
          serializeParameterValues(view.parameters, values),
        );
        if (source.kind === "builtin") {
          await applyBuiltin({
            variables: {
              builtinPresetId: source.builtin.id,
              tournamentStepId: stepId,
              parameters: serializedParams,
            },
          });
        } else {
          await applyPresetScript({
            variables: {
              presetScriptId: source.user.id,
              tournamentStepId: stepId,
              parameters: serializedParams,
            },
          });
        }
        notifications.show({
          title: view.appliedNotificationTitle,
          message: `"${view.label}" has been applied to this step.`,
          color: "green",
          autoClose: 3000,
        });
        modals.close(modalId);
        onApplied?.();
      } catch (error) {
        notifications.show({
          title: "Failed to apply",
          message:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          color: "red",
        });
        throw error;
      }
    };

    modals.open({
      modalId,
      title: `Apply "${view.label}"`,
      size: "md",
      centered: true,
      children: (
        <PresetParameterForm
          source={source}
          onCancel={() => modals.close(modalId)}
          onSubmit={handleApply}
        />
      ),
    });
  };

  const handleDuplicate = async (preset: BuiltinPreset) => {
    try {
      const result = await duplicateBuiltin({
        variables: { builtinPresetId: preset.id },
      });
      const created = result.data?.duplicateBuiltinPreset;
      if (!created) {
        throw new Error("Duplicate failed: no preset returned");
      }
      notifications.show({
        title: "Duplicated",
        message: `"${created.name}" added to your presets.`,
        color: "green",
        autoClose: 3000,
      });
      navigate(`/tournaments/preset-scripts/${created.id}`);
    } catch (error) {
      notifications.show({
        title: "Failed to duplicate",
        message:
          error instanceof Error ? error.message : "An unknown error occurred",
        color: "red",
      });
    }
  };

  if (builtinsLoading || presetsLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (builtinsError) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={16} />}
        title="Failed to load templates"
      >
        {builtinsError.message}
      </Alert>
    );
  }

  if (presetsError) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={16} />}
        title="Failed to load presets"
      >
        {presetsError.message}
      </Alert>
    );
  }

  return (
    <Tabs
      value={activeTab}
      onChange={(next) => {
        if (isPresetSelectorTab(next)) setActiveTab(next);
      }}
    >
      <Tabs.List>
        <Tabs.Tab value="templates" leftSection={<IconTemplate size={16} />}>
          Templates
        </Tabs.Tab>
        <Tabs.Tab value="user" leftSection={<IconUser size={16} />}>
          My presets
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="templates" pt="md">
        {builtinPresets.length === 0 ? (
          <Text size="sm" c="dimmed" py="md">
            No built-in templates are currently available.
          </Text>
        ) : (
          <Stack>
            {BUILTIN_PRESET_CATEGORY_ORDER.filter(
              (c) => grouped[c].length > 0,
            ).map((c) => (
              <Stack key={c} gap="xs">
                <Title order={5}>{BUILTIN_PRESET_CATEGORY_LABELS[c]}</Title>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {grouped[c].map((preset) => (
                    <Card key={preset.id} withBorder>
                      <Stack gap="xs" h="100%">
                        <Group justify="space-between" align="flex-start">
                          <Text fw={600}>{preset.displayName}</Text>
                          <Badge variant="light" size="xs">
                            {BUILTIN_PRESET_CATEGORY_LABELS[preset.category]}
                          </Badge>
                        </Group>
                        <Group gap="xs">
                          {preset.supportsGroupPinning === true && (
                            <Badge
                              variant="light"
                              color="cyan"
                              size="xs"
                              title="Teams can be pinned to specific groups during seeding"
                            >
                              Group pinning
                            </Badge>
                          )}
                          {preset.supportsSlotPinning === true && (
                            <Badge
                              variant="light"
                              color="cyan"
                              size="xs"
                              title="Teams can be pinned to specific game slots during seeding"
                            >
                              Slot pinning
                            </Badge>
                          )}
                          {preset.supportsPinning === false && (
                            <Badge
                              variant="light"
                              color="gray"
                              size="xs"
                              title="This preset does not support manual team pinning"
                            >
                              No pinning
                            </Badge>
                          )}
                        </Group>
                        <Text
                          size="sm"
                          c="dimmed"
                          style={{ flexGrow: 1 }}
                          lineClamp={3}
                        >
                          {preset.description}
                        </Text>
                        <Group gap="xs" justify="space-between">
                          <Button
                            variant="light"
                            size="xs"
                            onClick={() =>
                              openPresetModal({
                                kind: "builtin",
                                builtin: preset,
                              })
                            }
                          >
                            Apply
                          </Button>
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                aria-label={`More actions for ${preset.displayName}`}
                              >
                                <IconDots size={14} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconCopy size={14} />}
                                onClick={() => handleDuplicate(preset)}
                              >
                                Duplicate to my presets
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            ))}
          </Stack>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="user" pt="md">
        {userPresets.length === 0 ? (
          <Stack align="center" py="xl" gap="md">
            <Title order={4}>No custom presets yet</Title>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              Duplicate a template to start from a known-good format, or author
              one from scratch.
            </Text>
            <Group>
              <Button
                variant="default"
                onClick={() => setActiveTab("templates")}
              >
                Browse templates
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => navigate("/tournaments/preset-scripts/new")}
              >
                Create from scratch
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack>
            <Group justify="flex-end">
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => navigate("/tournaments/preset-scripts/new")}
              >
                New preset
              </Button>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
              {userPresets.map((preset) => (
                <Card key={preset.id} withBorder>
                  <Stack gap="xs" h="100%">
                    <Text fw={600}>{preset.name}</Text>
                    <Text
                      size="sm"
                      c="dimmed"
                      style={{ flexGrow: 1 }}
                      lineClamp={3}
                    >
                      {preset.description ?? "No description provided."}
                    </Text>
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() =>
                        openPresetModal({ kind: "user", user: preset })
                      }
                    >
                      Configure & Apply
                    </Button>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        )}
      </Tabs.Panel>
    </Tabs>
  );
};
